import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Chip, Pagination, Stack, Typography } from '@mui/material';
import { site } from 'apis';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import SiteAvatarImage from './Components/SiteAvatarImage';

const ITEMS_PER_PAGE = 20;

const getSiteAddress = (siteItem: any) => [siteItem?.address1, siteItem?.zipCode, siteItem?.city].filter(Boolean).join(', ');

const SitesView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);

    const sitesQuery = useQuery('publicSites', () => site.getPublicSites(), {
        suspense: false
    });

    const sites = useMemo(() => (Array.isArray(sitesQuery.data) ? sitesQuery.data : []), [sitesQuery.data]);
    const pageStartIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedSites = sites.slice(pageStartIndex, pageStartIndex + ITEMS_PER_PAGE);
    const pageCount = Math.max(1, Math.ceil(sites.length / ITEMS_PER_PAGE));
    const showingStart = sites.length ? pageStartIndex + 1 : 0;
    const showingEnd = Math.min(pageStartIndex + paginatedSites.length, sites.length);

    const handlePageChange = (_event: any, page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <Box sx={{ width: '100%', px: { xs: 1.5, md: 3 }, py: 2 }}>
            <Stack spacing={1.5} sx={{ maxWidth: 1480, mx: 'auto' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h2" sx={{ fontWeight: 900, color: '#111827' }}>
                        {t('Sites')}
                    </Typography>
                </Stack>

                {sitesQuery.isLoading ? (
                    <Typography sx={{ fontWeight: 700 }}>{t('Loading')}</Typography>
                ) : (
                    <Stack spacing={1.25}>
                        <Typography sx={{ color: '#475569', fontWeight: 700 }}>
                            {showingStart}-{showingEnd} / {sites.length}
                        </Typography>

                        {paginatedSites.map((siteItem: any) => (
                            <Box
                                key={siteItem.id}
                                onClick={() => navigate(`/public/sites/${siteItem.id}`)}
                                sx={{
                                    minHeight: 100,
                                    background: '#fff',
                                    border: '1px solid #dde5ef',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1.5, md: 2 },
                                    px: { xs: 1.5, md: 2 },
                                    py: 1.5,
                                    transition: 'box-shadow 160ms ease, transform 160ms ease',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)'
                                    }
                                }}
                            >
                                <SiteAvatarImage
                                    siteId={siteItem.id}
                                    alt={siteItem.name || t('Sites')}
                                    iconSize={34}
                                    sx={{
                                        width: { xs: 86, sm: 102 },
                                        height: { xs: 68, sm: 78 },
                                        borderRadius: 1,
                                        flex: '0 0 auto'
                                    }}
                                />

                                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {siteItem.countryAbbr && (
                                            <Chip
                                                size="small"
                                                label={siteItem.countryAbbr}
                                                sx={{ fontWeight: 800, background: '#ffe3ec', color: '#b01249' }}
                                            />
                                        )}
                                        <Chip
                                            size="small"
                                            label={`${siteItem.competitionCount || 0} ${t('Competitions')}`}
                                            sx={{ fontWeight: 800, background: '#e8f3ff', color: '#075aaa' }}
                                        />
                                    </Stack>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: 18, md: 22 },
                                            fontWeight: 900,
                                            lineHeight: 1.15,
                                            color: '#111827',
                                            overflowWrap: 'anywhere'
                                        }}
                                    >
                                        {siteItem.name || t('Sites')}
                                    </Typography>

                                    {getSiteAddress(siteItem) && (
                                        <Typography sx={{ color: '#667085', fontSize: { xs: 13, md: 15 }, fontWeight: 600 }}>
                                            {getSiteAddress(siteItem)}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        ))}

                        {!sites.length && <Typography sx={{ textAlign: 'center', py: 3, fontWeight: 700 }}>{t('NoDataFound')}</Typography>}

                        {sites.length > ITEMS_PER_PAGE && (
                            <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                                <Pagination count={pageCount} page={currentPage} color="primary" onChange={handlePageChange} />
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};

export default SitesView;
